'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { analytics } from 'lib/analytics/analytics';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from '../../utils/useGsapContext';
import { useMasterTimeline } from '../../utils/useMasterTimeline';
import gsap from 'gsap';
import CustomLink from 'components/shared/CustomLink';
import { CircleCheck, ReplayIcon } from 'assets/icons';
import { Sup } from '..';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useRetailStoreLocator } from 'hooks/navigation/useRetailStoreLocator';
import { usePreloadFrameSequence } from './usePreloadFrameSequence';
import { ResolutionConfig, ScreenSize } from './types';
import { drawImageSafe } from 'utils/drawImageSafe';
import styles from './HeroSectionV2.module.css';

type HeroSectionV2Props = {
  className?: string;
  onLoad?: () => void;
};

export const HeroSectionV2 = ({ className, onLoad }: HeroSectionV2Props) => {
  const t = useTranslations('home');
  const canvas = useRef<HTMLCanvasElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { region: country } = useRegionLocale();
  const { hasStores, openStoreLocator } = useRetailStoreLocator('hero');
  const [screenSize, setScreenSize] = useState<ScreenSize | null>(null);
  const [hasIntersected, setHasIntersected] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Responsive configuration
  const resolutionMap = useMemo<Record<ScreenSize, ResolutionConfig>>(
    () => ({
      sm: {
        tag: 'w_800',
        width: 800,
        height: 1422,
        frameStep: 1,
        dir_tag: 'sm',
        duration: 9,
        textAnimationStartTime: 0.5,
        frameCount: 274,
      },
      lg: {
        tag: 'w_1920',
        width: 1920,
        height: 1080,
        frameStep: 1,
        dir_tag: 'lg',
        duration: 9,
        textAnimationStartTime: 0.5,
        frameCount: 287,
      },
      xl: {
        tag: 'w_2560',
        width: 2560,
        height: 1440,
        frameStep: 1,
        dir_tag: 'lg',
        duration: 9,
        textAnimationStartTime: 0.5,
        frameCount: 287,
      },
    }),
    [],
  );

  const images = usePreloadFrameSequence(
    '/web_v2/sequence/landing_hero-v4/',
    screenSize,
    resolutionMap,
    onLoad,
  );
  const animation = useRef<gsap.core.Timeline | null>(null);
  const gsapContext = useGsapContext();
  const { schedule } = useMasterTimeline();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 768) setScreenSize('sm');
    else if (window.innerWidth < 2000) setScreenSize('lg');
    else setScreenSize('xl');
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setHasIntersected(true);
          observer.disconnect(); // Run only once
        }
      },
      {
        threshold: [0.5], // 50% visibility
      },
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!screenSize || images.length === 0 || !hasIntersected) return;
    animationInit();
  }, [screenSize, images, hasIntersected]);

  const animationInit = () => {
    if (
      !canvas.current ||
      !contentRef.current ||
      !screenSize ||
      images.length === 0
    )
      return;
    const frameCount = resolutionMap[screenSize].frameCount;
    gsap.set(contentRef.current, { autoAlpha: 0 });

    const canvasEl = canvas.current;
    const context = canvasEl.getContext('2d');
    if (!context) return;
    canvasEl.width = resolutionMap[screenSize].width;
    canvasEl.height = resolutionMap[screenSize].height;

    drawImageSafe(context, images[0], 0, 0);

    // GSAP animation setup
    const frames = { frame: 0 };
    const render = () => {
      const frameIdx = Math.floor(frames.frame);
      const img = images[frameIdx];
      if (!img || !img.complete || img.naturalWidth === 0) return;
      if (!context) return;
      requestAnimationFrame(() => {
        context.clearRect(0, 0, canvasEl.width, canvasEl.height);
        drawImageSafe(context, img, 0, 0);
      });
    };

    function startAnimation() {
      if (animation.current) {
        animation.current.progress(0);
        animation.current.play();
        return;
      }
      gsapContext.add(() => {
        animation.current = gsap.timeline({
          defaults: { duration: 1, ease: 'none' },
          paused: true,
        });

        animation.current.fromTo(
          frames,
          { frame: 1 },
          {
            frame: frameCount - 1,
            duration: resolutionMap[screenSize ?? 'lg'].duration,
            snap: 'frame',
            ease: 'linear',
            onUpdate: render,
          },
        );

        animation.current.fromTo(
          contentRef.current,
          { autoAlpha: '0', duration: 0.5, ease: 'Power2.easeIn' },
          { autoAlpha: '1' },
          resolutionMap[screenSize ?? 'lg'].textAnimationStartTime,
        );

        if (animation.current) {
          schedule(
            () => animation.current && animation.current.play(0),
            'hero-section-v2',
          );
        }

        animation.current.play();
      });
    }
    startAnimation();
  };

  const replayAnimation = () => {
    analytics.track('Landing-hero-replay - CLICK', { clicked: true });
    if (!animation.current) return;
    animation.current.pause();
    animation.current.progress(0);
    animation.current.play();
  };

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={sectionRef}
      role="banner"
      aria-label={t('home.heroSection.ariaLabel.heroSection')}
    >
      <canvas
        ref={canvas}
        aria-label={t('home.heroSection.ariaLabel.renderCanvas')}
        role="img"
      ></canvas>
      <div
        className={styles.content}
        ref={contentRef}
        aria-labelledby="hero-heading"
      >
        <h2>{t('home.heroV2.headA')}</h2>
        <h1 id="hero-heading">
          {t('home.heroV2.headB')}
          <Sup>&reg;</Sup>
        </h1>
        <p>{t('home.heroV2.description')}</p>
        <div className={styles.ctaContainer}>
          {hasStores ? (
            <button
              onClick={openStoreLocator}
              className={`${styles.cta} ${styles.store} ${styles.storeLocatorBtn} ${styles.smNone}`}
              data-buttontype="ring homepage buy at store"
              style={{ background: '#000000', color: '#FFFFFF' }}
              aria-label={t('home.heroSection.ariaLabel.findStore')}
            >
              <p>{t('home.heroV2.ctaA')}</p>
            </button>
          ) : null}
          <CustomLink
            className={`${styles.cta} ${styles.primary} ${styles.smNone} ${styles.noMbl}`}
            href="/ring/buy"
            data-buttontype="ring homepage buy"
            onClick={() => {
              analytics.track('Buy Now - CLICK', { clicked: true });
            }}
            aria-label={t('home.heroSection.ariaLabel.buyNow')}
          >
            {t('home.heroV2.ctaB')}
          </CustomLink>
        </div>
        {country === 'US' ? (
          <div
            className={styles.hsaInfo}
            role="note"
            aria-label={t('home.heroSection.ariaLabel.fsaHsa')}
          >
            <CircleCheck fill="#cecece" aria-hidden="true" />
            {t('home.heroV2.fsaHsa')}
          </div>
        ) : null}
      </div>
      <div className={styles.smCtas}>
        <div className={styles.smRow}>
          {hasStores ? (
            <button
              onClick={openStoreLocator}
              className={`${styles.cta} ${styles.primary} ${styles.storeLocatorBtn}`}
              data-buttontype="ring homepage buy at store"
              style={{
                background: '#000000',
                color: '#FFFFFF',
                border: '1px solid rgba(255, 255, 255, 0.20)',
              }}
              aria-label={t('home.heroSection.ariaLabel.findStore')}
            >
              <p>{t('home.heroV2.ctaA')}</p>
            </button>
          ) : null}
          <CustomLink
            className={`${styles.cta} ${styles.primary}`}
            href="/ring/buy"
            data-buttontype="ring homepage buy mobile"
            onClick={() => {
              analytics.track('Buy Now - CLICK', { clicked: true });
            }}
            aria-label={t('home.heroSection.ariaLabel.buyNow')}
          >
            {t('home.heroV2.ctaB')}
          </CustomLink>
        </div>
      </div>
      <button
        onClick={replayAnimation}
        className={styles.replayBtn}
        data-buttontype="animation replay"
        aria-label={t('home.heroSection.ariaLabel.replay')}
        title={t('home.heroSection.ariaLabel.replayTitle')}
      >
        <ReplayIcon
          fill="#ffffff"
          style={{
            height: '16px',
            width: 'auto',
            transform: 'translateY(1.5px)',
            opacity: '0.7',
          }}
        />
      </button>
    </section>
  );
};
